import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/tokenStorage';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPaymentsTab = ({ downloadCSV }) => {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [packageFilter, setPackageFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');

  useEffect(() => {
    const token = getToken();
    axios.get(`${API}/payments/transactions`, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } })
      .then(res => setTxns(res.data || []))
      .catch(() => setTxns([]))
      .finally(() => setLoading(false));
  }, []);

  const now = Date.now();
  const dayMs = 86400000;

  const filtered = useMemo(() => {
    return txns.filter(t => {
      const q = search.trim().toLowerCase();
      if (q && !(
        (t.email || '').toLowerCase().includes(q) ||
        (t.name || '').toLowerCase().includes(q) ||
        (t.session_id || '').toLowerCase().includes(q) ||
        (t.user_id || '').toLowerCase().includes(q)
      )) return false;
      if (statusFilter !== 'all' && t.payment_status !== statusFilter) return false;
      if (packageFilter !== 'all' && t.package_id !== packageFilter) return false;
      if (dateRange !== 'all') {
        const created = new Date(t.created_at || 0).getTime();
        if (dateRange === '7d' && now - created > 7 * dayMs) return false;
        if (dateRange === '30d' && now - created > 30 * dayMs) return false;
        if (dateRange === '90d' && now - created > 90 * dayMs) return false;
      }
      return true;
    });
  }, [txns, search, statusFilter, packageFilter, dateRange, now]);

  const totals = useMemo(() => {
    const paid = filtered.filter(t => t.payment_status === 'paid');
    return {
      count: filtered.length,
      paidCount: paid.length,
      paidTotal: paid.reduce((s, t) => s + (Number(t.amount) || 0), 0),
    };
  }, [filtered]);

  const packageLabel = (id) => ({ envoy: 'Gold Envoy $20', single: 'Single $190', couples: 'Couples $290' }[id] || id || '—');
  const statusBadge = (s) => {
    const map = {
      paid: { bg: '#d1fae5', fg: '#065f46', label: 'Paid' },
      initiated: { bg: '#fef3c7', fg: '#92400e', label: 'Initiated' },
      failed: { bg: '#fee2e2', fg: '#991b1b', label: 'Failed' },
      canceled: { bg: '#f3f4f6', fg: '#374151', label: 'Cancelled' },
      expired: { bg: '#f3f4f6', fg: '#374151', label: 'Expired' },
    };
    const c = map[s] || { bg: '#e5e7eb', fg: '#374151', label: s || '—' };
    return <span style={{ background: c.bg, color: c.fg, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>{c.label}</span>;
  };

  const exportCsv = () => {
    const header = ['Date', 'Name', 'Email', 'Package', 'Amount AUD', 'Status', 'Provider', 'Session ID'];
    const rows = filtered.map(t => [
      new Date(t.created_at || 0).toLocaleString('en-AU'),
      t.name || '',
      t.email || '',
      packageLabel(t.package_id),
      Number(t.amount || 0).toFixed(2),
      t.payment_status || '',
      t.provider || 'stripe',
      t.session_id || '',
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard-content" data-testid="admin-payments-tab">
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ margin: 0 }}>Payments</h2>
          <button className="btn btn-sm" style={{ fontSize: '12px', padding: '6px 12px' }} onClick={exportCsv} data-testid="export-payments-btn">Export CSV</button>
        </div>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '10px', border: '1px solid #dbeafe' }}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Transactions</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e40af' }}>{totals.count}</div>
          </div>
          <div style={{ background: '#d1fae5', padding: '16px', borderRadius: '10px', border: '1px solid #a7f3d0' }}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Paid</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#065f46' }}>{totals.paidCount}</div>
          </div>
          <div style={{ background: '#fef3c7', padding: '16px', borderRadius: '10px', border: '1px solid #fde68a' }}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Revenue (AUD)</div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#92400e' }}>${totals.paidTotal.toFixed(2)}</div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input type="text" placeholder="Search name, email, session…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 220px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
            data-testid="payments-search" />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="filter-select" data-testid="payments-status-filter">
            <option value="all">All statuses</option>
            <option value="paid">Paid</option>
            <option value="initiated">Initiated</option>
            <option value="failed">Failed</option>
            <option value="canceled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
          <select value={packageFilter} onChange={e => setPackageFilter(e.target.value)} className="filter-select" data-testid="payments-package-filter">
            <option value="all">All packages</option>
            <option value="envoy">Gold Envoy $20</option>
            <option value="single">Single $190</option>
            <option value="couples">Couples $290</option>
          </select>
          <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="filter-select" data-testid="payments-date-filter">
            <option value="all">All time</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading payments…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            No payments match your filters. {txns.length === 0 && 'No transactions recorded yet.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', background: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '12px' }}>Date</th>
                  <th style={{ padding: '12px' }}>Member</th>
                  <th style={{ padding: '12px' }}>Package</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '12px' }}>Status</th>
                  <th style={{ padding: '12px' }}>Provider</th>
                  <th style={{ padding: '12px' }}>Session</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.session_id || t._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px', whiteSpace: 'nowrap', color: '#374151' }}>{t.created_at ? new Date(t.created_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ fontWeight: 600 }}>{t.name || '—'}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{t.email || '—'}</div>
                    </td>
                    <td style={{ padding: '12px' }}>{packageLabel(t.package_id)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>${Number(t.amount || 0).toFixed(2)}</td>
                    <td style={{ padding: '12px' }}>{statusBadge(t.payment_status)}</td>
                    <td style={{ padding: '12px', textTransform: 'capitalize', color: '#6b7280' }}>{t.provider || 'stripe'}</td>
                    <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '11px', color: '#6b7280', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.session_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPaymentsTab;
